import { canonicalOrigin, ctaIds, CtaId, faqIds, Location, PagePath, publicPages, publicPath, Service, services } from './catalog';
import { captureAttribution } from './attribution';
import { clearOptionalStorage, getConsent, Consent } from './consent';

type Contact = { cta_id: CtaId; button_location: Location; service_name?: Service };
type Form = { form_id: 'project_contact'; service_name?: Service };
export interface EventMap {
  page_view: { page_path: PagePath; page_title: string };
  click_whatsapp: Contact; click_quote: Contact; click_email: Contact; click_phone: Contact;
  view_service: { service_name: Service; section_id: 'solucoes' };
  click_service: { service_name: Service; cta_id: CtaId };
  click_product: { product_name: 'zzblu' | 'zzblu3d'; button_location: Location; cta_id: CtaId };
  form_start: Form;
  generate_lead: Form & { contact_method: 'form' };
  form_error: Form & { error_type: 'validation' | 'network' | 'server' | 'spam' };
  scroll_depth: { percent_scrolled: 25 | 50 | 75 | 90 };
  faq_open: { faq_id: typeof faqIds[number] };
  click_social: { social_network: 'instagram' | 'linkedin'; button_location: Location; cta_id: CtaId };
}
export type EventName = keyof EventMap;
const schemas: Record<EventName, Record<string, readonly (string | number)[]>> = {
  page_view: { page_path: Object.keys(publicPages), page_title: Object.values(publicPages) },
  click_whatsapp: {}, click_quote: {}, click_email: {}, click_phone: {},
  view_service: { service_name: Object.keys(services), section_id: ['solucoes'] },
  click_service: { service_name: Object.keys(services), cta_id: ctaIds },
  click_product: { product_name: ['zzblu', 'zzblu3d'], button_location: ['products'], cta_id: ctaIds },
  form_start: { form_id: ['project_contact'], service_name: Object.keys(services) },
  generate_lead: { form_id: ['project_contact'], service_name: Object.keys(services), contact_method: ['form'] },
  form_error: { form_id: ['project_contact'], service_name: Object.keys(services), error_type: ['validation', 'network', 'server', 'spam'] },
  scroll_depth: { percent_scrolled: [25, 50, 75, 90] },
  faq_open: { faq_id: faqIds },
  click_social: { social_network: ['instagram', 'linkedin'], button_location: ['qrcode', 'footer'], cta_id: ctaIds },
};
for (const name of ['click_whatsapp', 'click_quote', 'click_email', 'click_phone'] as const) schemas[name] = {
  cta_id: ctaIds, button_location: ['header', 'hero', 'services', 'products', 'contact', 'footer', 'qrcode'], service_name: Object.keys(services),
};
interface Runtime {
  initialized: boolean; loaded: boolean; consent: Consent; path: PagePath | null;
  pageSent: string | null; seen: Set<string>; consentListeners: Set<(allowed: boolean) => void>;
  debug: Record<string, unknown>[]; clarityAllowed: boolean;
}
declare global {
  interface Window {
    dataLayer?: unknown[];
    clarity?: ((...args: unknown[]) => void) & { q?: unknown[][] };
    antaresAnalytics?: Runtime;
    antaresAnalyticsConsent?: boolean;
    antaresOnConsentChange?: (callback: (allowed: boolean) => void) => void;
  }
}
export function runtime(): Runtime {
  return window.antaresAnalytics ?? (window.antaresAnalytics = {
    initialized: false, loaded: false, consent: getConsent(), path: null, pageSent: null,
    seen: new Set(), consentListeners: new Set(), debug: [], clarityAllowed: false,
  });
}
export function mode(): 'live' | 'debug' | 'off' {
  if (typeof window === 'undefined') return 'off';
  if (process.env.REACT_APP_ANALYTICS_DEBUG === 'true') return 'debug';
  return process.env.NODE_ENV === 'production' &&
    ['antarestecnologiagta.com.br', 'www.antarestecnologiagta.com.br'].includes(window.location.hostname) &&
    /^GTM-[A-Z0-9]+$/.test(process.env.REACT_APP_GTM_ID || '') ? 'live' : 'off';
}
export function sanitizeEvent(name: string, params: unknown): Record<string, string | number> | null {
  if (!Object.prototype.hasOwnProperty.call(schemas, name) || !params || typeof params !== 'object') return null;
  const schema = schemas[name as EventName];
  const input = params as Record<string, unknown>;
  const clean: Record<string, string | number> = {};
  for (const [key, values] of Object.entries(schema)) {
    const value = input[key];
    if (value === undefined && key === 'service_name' && !['view_service', 'click_service'].includes(name)) continue;
    if (!values.includes(value as string)) return null;
    clean[key] = value as string | number;
  }
  return clean;
}
function push(value: unknown) { (window.dataLayer ?? (window.dataLayer = [])).push(value); }
export function claritySafe(href: string, referrer: string): boolean {
  try {
    const url = new URL(href);
    if (!publicPath(url.pathname) || url.search || (url.hash && !['#inicio', '#conteudo', '#solucoes', '#produtos', '#antares', '#contato', '#perguntas'].includes(url.hash))) return false;
    if (!referrer) return true;
    const ref = new URL(referrer);
    // Clarity reads the browser URL/referrer independently: don't load it on ambiguous entries.
    return !ref.search && !ref.hash && ref.pathname === '/' && ref.hostname === new URL(canonicalOrigin).hostname;
  } catch { return false; }
}
export function initialize() {
  if (typeof window === 'undefined') return;
  const state = runtime();
  if (state.initialized) return;
  state.initialized = true;
  if (state.consent !== 'granted') clearOptionalStorage();
  state.clarityAllowed = claritySafe(window.location.href, document.referrer);
  window.antaresAnalyticsConsent = state.consent === 'granted';
  window.antaresOnConsentChange = callback => { state.consentListeners.add(callback); };
  // Google's documented on-page fallback. GTM consent template also sets defaults synchronously.
  function command(..._args: unknown[]) { push(arguments); }
  command('consent', 'default', { analytics_storage: 'denied', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' });
}
function loadContainer() {
  const state = runtime();
  if (mode() !== 'live' || state.loaded || state.consent !== 'granted' || !state.path) return;
  state.loaded = true;
  // Queue consent before a Clarity tag can execute. No vendor script is loaded here except GTM.
  if (!window.clarity) {
    const queue = (...args: unknown[]) => { (queue.q ?? (queue.q = [])).push(args); };
    queue.q = [] as unknown[][];
    window.clarity = queue;
  }
  window.clarity('consentv2', { analytics_Storage: 'granted', ad_Storage: 'denied' });
  push({ 'gtm.start': Date.now(), event: 'gtm.js', analytics_consent: true, clarity_allowed: state.clarityAllowed });
  push({ event: 'antares_consent', analytics_consent: true, clarity_allowed: state.clarityAllowed });
  const script = document.createElement('script');
  script.id = 'antares-gtm'; script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${process.env.REACT_APP_GTM_ID}`;
  script.onerror = () => { /* A blocked tracker must never block the site. */ };
  document.head.appendChild(script);
}
export function updateConsent(consent: Consent, reload = () => window.location.reload()) {
  initialize();
  const state = runtime();
  state.consent = consent;
  window.antaresAnalyticsConsent = consent === 'granted';
  state.consentListeners.forEach(callback => { try { callback(consent === 'granted'); } catch { /* Isolate tag errors. */ } });
  if (consent !== 'granted') {
    state.pageSent = null; state.seen.clear(); state.debug.length = 0;
    clearOptionalStorage();
    if (state.loaded) {
      push({ event: 'antares_consent', analytics_consent: false, clarity_allowed: false });
      try { window.clarity?.('consentv2', { analytics_Storage: 'denied', ad_Storage: 'denied' }); } catch { /* Vendor optional. */ }
      // Removing a <script> does not stop already running SDKs. Reload into denied state.
      reload();
    }
    return;
  }
  if (state.path) viewPage(state.path);
}
export function track<N extends EventName>(name: N, params: EventMap[N], once?: string): boolean {
  try {
    if (typeof window === 'undefined') return false;
    const state = runtime();
    if (state.consent !== 'granted' || getConsent() !== 'granted' || !state.path || mode() === 'off') return false;
    const clean = sanitizeEvent(name, params);
    if (!clean || (once && state.seen.has(once))) return false;
    const attribution = captureAttribution(window.location.href, document.referrer);
    const payload = { event: 'antares_event', event_name: name, analytics: {
      ...clean, page_path: state.path, page_title: publicPages[state.path], page_location: canonicalOrigin + state.path,
      page_referrer: attribution?.session.referrer_domain && attribution.session.referrer_domain !== 'other'
        ? `https://${attribution.session.referrer_domain}/` : '',
      traffic_source: attribution?.session.source || 'direct_unknown', traffic_medium: attribution?.session.medium || 'none',
      campaign_name: attribution?.session.utms.utm_campaign || '', campaign_content: attribution?.session.utms.utm_content || '',
      campaign_term: attribution?.session.utms.utm_term || '',
    } };
    if (once) state.seen.add(once);
    if (mode() === 'debug') {
      state.debug.push(payload); if (state.debug.length > 100) state.debug.shift();
      console.info('[Antares analytics: local only]', payload);
    } else { push({ analytics: null }); push(payload); }
    return true;
  } catch { return false; }
}
export function viewPage(pathname: string) {
  initialize();
  const state = runtime();
  const path = publicPath(pathname);
  if (state.path !== path) { state.path = path; state.pageSent = null; state.seen.clear(); }
  if (!path || state.consent !== 'granted') return;
  const attribution = captureAttribution(window.location.href, document.referrer);
  // Safe defaults must exist before GTM executes any Google configuration tag.
  if (mode() === 'live') push({ analytics_page: {
    page_location: canonicalOrigin + path, page_title: publicPages[path],
    page_referrer: attribution?.session.referrer_domain && attribution.session.referrer_domain !== 'other'
      ? `https://${attribution.session.referrer_domain}/` : '',
    campaign_source: attribution?.session.source === 'direct_unknown' ? '' : attribution?.session.source || '',
    campaign_medium: attribution?.session.medium === 'none' ? '' : attribution?.session.medium || '',
    campaign_name: attribution?.session.utms.utm_campaign || '',
    campaign_content: attribution?.session.utms.utm_content || '',
    campaign_term: attribution?.session.utms.utm_term || '',
  } });
  loadContainer();
  if (state.pageSent !== path && track('page_view', { page_path: path, page_title: publicPages[path] })) state.pageSent = path;
}
